<template>
  <Stack>
    <div class="body">
      <Stack class="main">
        <header class="inbox-header">
          <Title>Task Inbox</Title>
          <Buttons class="inbox-buttons">
            <Button
              v-for="button in buttons"
              :key="button.label"
              :icon-before="button.icon"
              :shade="button.shade"
              spacing="tight"
              @click="handleAction(button.letter)"
            >
              {{ button.label }}
              <!-- <Keystroke
                :shade="button.shade"
                :letter="button.letter"
              /> -->
            </Button>
          </Buttons>
        </header>

        <LoaderWithText v-if="isLoading" />

        <template v-else>
          <Stack
            v-if="recommendationItems.length > 0 && !allProcessed"
            gap="0"
          >
            <Stack
              v-for="(item, index) in recommendationItems"
              :key="item.id"
            >
              <RecommendationItem
                :item="item"
                @hidden="onItemHidden"
              />
            </Stack>
          </Stack>

          <Callout
            v-else
            icon-before="fas fa-circle-check"
            title="Great job!"
            status="success"
            variant="empty"
          >
            You have no outstanding tasks to sort out.
          </Callout>
        </template>
      </Stack>

      <aside class="side">
        <template v-if="taskStore.recommendations.hasFetched">
          <Menu>
            <MenuLabel>Type</MenuLabel>
            <MenuList>
              <MenuItem
                v-for="menuItem in menuItems"
                :key="menuItem.id"
                :icon="menuItem.icon"
                :count="menuItem.count"
              >
                {{ menuItem.label }}
              </MenuItem>
            </MenuList>
          </Menu>

          <MenuDivider />

          <Menu>
            <MenuLabel>
              Person
              <TextLink
                v-if="hasEntities"
                @click="resetEntities"
              >
                Reset
              </TextLink>
            </MenuLabel>
            <MenuList>
              <MenuItem
                v-for="person in people"
                :key="person.entity__id"
                :active="hasEntity(person.entity__external_id)"
                @click="togglePerson(person.entity__external_id)"
              >
                {{ person.entity__name }}
              </MenuItem>
            </MenuList>
          </Menu>
        </template>
      </aside>
    </div>
  </Stack>
</template>

<script setup>
import { onMounted, computed } from 'vue';
import RecommendationItem from './RecommendationItem.vue';
import { Buttons, Button, Callout, LoaderWithText, Menu, MenuList, MenuItem, MenuLabel, MenuDivider, Stack, TextLink, Title } from '@stakeui';
import { useTaskStore } from '@frontend/ui/stores/taskStore.js';

const taskStore = useTaskStore();

const isLoading = computed(() => {
  return taskStore.recommendations.isFetching || !taskStore.recommendations.hasFetched;
});

const recommendationItems = computed(() => taskStore.recommendations?.items || []);
const allProcessed = computed(() => taskStore.recommendations.items.length === taskStore.recommendations.processedItems.length);

const buttons = computed(() => {
  return [
    // Turning this off for simplicity, since tasks can be assigned in edit mode.
    // Can implement later if needed
    /* {
      id: 'assign',
      label: 'Assign',
      icon: 'fas fa-arrow-circle-right',
      shade: 'info',
      letter: 'a',
    }, */
    {
      id: 'snooze',
      label: 'Snooze',
      icon: 'fas fa-hourglass-half',
      shade: 'warning',
      letter: 's',
    },
    {
      id: 'dismiss',
      label: 'Dismiss',
      icon: 'fas fa-ban',
      shade: 'danger',
      letter: 'd',
    },
    {
      id: 'accept',
      label: 'Accept',
      icon: 'fas fa-check',
      shade: 'success',
      letter: 'f',
    },
  ];
});

const menuItems = computed(() => [
  {
    id: 'confirm-info',
    icon: 'fas fa-check-double',
    label: 'Confirm Information',
    count: 31,
  },
  {
    id: 'gather-data',
    icon: 'fas fa-compress-arrows-alt',
    label: 'Gather Missing Data',
    count: 12,
  },
  {
    id: 'plan-future',
    icon: 'fas fa-seedling',
    label: 'Plan the Future',
    count: 8,
  },
  {
    id: 'estate',
    icon: 'fas fa-home',
    label: 'Estate',
    count: 7,
  },
  {
    id: 'insurance',
    icon: 'fas fa-file-medical',
    label: 'Check Insurance',
    count: 5,
  },
  {
    id: 'tax',
    icon: 'fas fa-building',
    label: 'Optimize Tax',
    count: 29,
  },
]);

const people = computed(() => taskStore.recommendations?.entities || []);

const hasEntities = computed(() => {
  return taskStore.recommendations.filters.entities.length > 0;
});

const togglePerson = externalId => {
  taskStore.toggleFilterValue('recommendations', 'entities', externalId);
  taskStore.fetchRecommendations();
};

const hasEntity = externalId => {
  return taskStore.hasFilterValue('recommendations', 'entities', externalId);
};

const resetEntities = () => {
  taskStore.resetFilterArray('recommendations', 'entities');
};

function handleAction(letter) {
  if (items.value.length > 0) {
    items.value[0].hide = true;
  }
}

function onItemHidden(itemId) {
  console.log(`Item with id ${itemId} is hidden`);
  // Optionally remove it from items array:
  // items.value = items.value.filter(item => item.id !== itemId);
}

const performSearch = () => {
  taskStore.fetchRecommendations();
};

onMounted(() => {
  if (taskStore.recommendations.hasFetched) {
    return;
  }

  performSearch();
});
</script>

<style scoped>
.inbox-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.inbox-buttons {
  display: none;
}

.body {
  gap: 2rem;
  display: grid;
  grid-template-columns: 1fr;
}

.side {
  display: none;
}
</style>
